import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useDebounce } from 'react-use';
import { useLocalStorageState } from '../../../../../hooks';
import { Recency } from '../utils';

type SearchContextType = {
  searchTerm: string;
  debouncedSearchTerm: string;
  setSearchTerm: (term: string) => void;
  recency: Recency;
  setRecency: (recency: Recency) => void;
  annotatedOnly: boolean;
  setAnnotatedOnly: (value: boolean) => void;
};

const SearchContext = createContext<SearchContextType | undefined>(undefined);

type SearchProviderProps = {
  children: ReactNode;
};

function SearchProvider({ children }: SearchProviderProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [recency, setRecency] = useLocalStorageState<Recency>(
    'labResults.recency',
    'last_180_days',
  );
  const [annotatedOnly, setAnnotatedOnly] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  useDebounce(
    () => {
      setDebouncedSearchTerm(searchTerm);
    },
    500,
    [searchTerm],
  );

  return (
    <SearchContext.Provider
      value={{
        searchTerm,
        setSearchTerm,
        debouncedSearchTerm,
        recency,
        setRecency,
        annotatedOnly,
        setAnnotatedOnly,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
}

function useSearchContext() {
  const context = useContext(SearchContext);

  if (!context) {
    throw new Error('useSearchContext must be used within a SearchProvider');
  }

  return context;
}

const Search = {
  Provider: SearchProvider,
};

export { Search, useSearchContext };
